import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { chatActionService } from "../../services/api";
import { labelForStaffRole } from "../../constants/staffRoles";

const AddChatMembersModal = ({
  isOpen,
  onClose,
  pitchId,
  cid,
  channelId,
  channel,
  onMembersAdded,
}) => {
  // Pitch channels keep the proven pitch endpoints; every other club<->agent
  // channel (direct_*) is addressed by cid (D-008).
  const usePitch = Boolean(pitchId);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAccounts, setSelectedAccounts] = useState([]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStaffOptions([]);
      setSearchTerm("");
      setSelectedAccounts([]);
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isSubmitting) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, isSubmitting, onClose]);

  useEffect(() => {
    if (!isOpen || (!pitchId && !cid)) {
      return;
    }

    let cancelled = false;

    const fetchStaff = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = usePitch
          ? await chatActionService.getPitchStaff(pitchId)
          : await chatActionService.getChannelStaff(cid);

        if (cancelled) {
          return;
        }

        if (!response.success) {
          throw new Error(response.error || "Failed to load staff");
        }

        // T039: the endpoint sends the raw role_type enum ("assistant_coach").
        // Humanize once here, through the shared staff-role catalog, so the
        // pill, the confirm sentence AND the search term all read the same
        // label instead of three ad-hoc replaces.
        setStaffOptions(
          (response.results || []).map((item) => ({
            ...item,
            role: labelForStaffRole(item.role) || item.role,
          }))
        );
      } catch (err) {
        console.error("AddChatMembersModal: fetch error", err);
        if (!cancelled) {
          setError(err.message || "Failed to load staff");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchStaff();

    return () => {
      cancelled = true;
    };
  }, [isOpen, pitchId, cid, usePitch]);

  const handleToggleAccount = (accountId, disabled) => {
    if (disabled) {
      return;
    }

    setSelectedAccounts((prev) =>
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId]
    );
  };

  const filteredStaff = useMemo(() => {
    if (!searchTerm.trim()) {
      return staffOptions;
    }

    const term = searchTerm.trim().toLowerCase();

    return staffOptions.filter((item) => {
      const name = item.name || "";
      const email = item.email || "";
      const role = item.role || "";

      return (
        name.toLowerCase().includes(term) ||
        email.toLowerCase().includes(term) ||
        role.toLowerCase().includes(term)
      );
    });
  }, [staffOptions, searchTerm]);

  const selectedCount = selectedAccounts.length;

  const selectedStaff = useMemo(
    () => staffOptions.filter((item) => selectedAccounts.includes(item.account_id)),
    [staffOptions, selectedAccounts],
  );

  const handleSubmit = async () => {
    if (!selectedCount) {
      toast.error("Select at least one staff member");
      return;
    }

    if (!pitchId && !cid) {
      toast.error("Missing channel information");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = usePitch
        ? await chatActionService.addPitchMembers(pitchId, {
            channel_id: channelId,
            account_ids: selectedAccounts,
          })
        : await chatActionService.addChannelMembers(cid, selectedAccounts);

      if (!response.success) {
        throw new Error(response.error || "Failed to add members");
      }

      const addedCount = response.added_members?.length || 0;
      const skippedCount = response.skipped?.length || 0;

      if (addedCount) {
        toast.success(
          skippedCount
            ? `Added ${addedCount} member${addedCount > 1 ? "s" : ""}. ${skippedCount} skipped.`
            : `Added ${addedCount} member${addedCount > 1 ? "s" : ""}.`
        );
      } else {
        toast(
          skippedCount
            ? `No new members added. ${skippedCount} staff already in chat.`
            : "No members added"
        );
      }

      // Optimistically refresh channel members
      try {
        if (channel?.queryMembers) {
          await channel.queryMembers({}, {}, { limit: 30 });
        }
      } catch (refreshError) {
        console.warn("Failed to refresh channel members", refreshError);
      }

      onMembersAdded?.(response);
      onClose();
    } catch (err) {
      console.error("AddChatMembersModal: submit error", err);
      toast.error(err.message || "Failed to add members");
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalContent = (
    // #34: bottom sheet under md (bottom-anchored, rounded-top, full-width),
    // centered dialog from md up — the desktop classes are preserved verbatim
    // behind md: prefixes so the desktop modal is unchanged.
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={isSubmitting ? undefined : onClose}
        aria-hidden="true"
      />

      <div className="relative bg-white rounded-t-2xl md:rounded-2xl shadow-xl w-full md:max-w-2xl md:mx-4 max-h-[85vh] flex flex-col">
        {/* Mobile grabber (design bottom-sheet chrome). */}
        <div className="md:hidden flex justify-center pt-2.5 pb-1">
          <span className="h-1 w-10 rounded-full bg-[#E6EAF2]" />
        </div>
        <div className="border-b border-[#E5E7EB] px-6 py-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-[#111927]">
                Add member
              </h3>
              <p className="text-sm text-[#6C737F] mt-1">
                New members are from your club and see the full conversation
                history.
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="text-[#6C737F] hover:text-[#111927] transition-colors disabled:opacity-50"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="mt-4 relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search your club…"
              className="w-full px-4 py-2 pl-10 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2283FF]"
              disabled={isLoading}
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6C737F]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="py-10 text-center text-sm text-[#6C737F]">
              Loading staff members...
            </div>
          ) : error ? (
            <div className="py-10 text-center text-sm text-red-600">
              {error}
            </div>
          ) : filteredStaff.length ? (
            <ul className="space-y-3">
              {filteredStaff.map((staff) => {
                const isDisabled = Boolean(staff.already_in_channel);
                const isSelected = selectedAccounts.includes(staff.account_id);

                return (
                  <li
                    key={staff.account_id}
                    className={`border border-[#E5E7EB] rounded-xl px-4 py-3 flex items-start gap-4 transition-colors ${
                      isDisabled
                        ? "bg-[#F9FAFB] opacity-75 cursor-not-allowed"
                        : isSelected
                        ? "border-[#2283FF] bg-[#F4F8FF]"
                        : "hover:border-[#D0D5DD]"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleToggleAccount(staff.account_id, isDisabled)}
                      className={`mt-1 w-4 h-4 border rounded flex items-center justify-center transition-colors ${
                        isDisabled
                          ? "border-[#D0D5DD] bg-[#EAECF0]"
                          : isSelected
                          ? "border-[#2283FF] bg-[#2283FF]"
                          : "border-[#D0D5DD] bg-white"
                      }`}
                      aria-pressed={isSelected}
                      disabled={isDisabled}
                    >
                      {isSelected && !isDisabled && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </button>

                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-sm font-medium text-[#111927]">
                          {staff.name || staff.email || `Account ${staff.account_id}`}
                        </span>
                        {staff.role && (
                          <span className="px-2 py-0.5 text-xs bg-[#F4F4F4] text-[#6C737F] rounded-full">
                            {staff.role}
                          </span>
                        )}
                        {staff.already_in_channel && (
                          <span className="px-2 py-0.5 text-xs bg-[#E0EAFF] text-[#1C64F2] rounded-full">
                            Already in chat
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#6C737F] mt-1">
                        {staff.email || "Email not available"}
                      </div>
                      {!staff.can_contact_agents && (
                        <div className="mt-2 text-xs text-[#B42318] bg-[#FEE4E2] inline-flex px-2 py-1 rounded">
                          This staff member cannot contact agents yet.
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="py-10 text-center text-sm text-[#6C737F]">
              No staff members found.
            </div>
          )}
        </div>

        {selectedCount > 0 && (
          <div className="px-6 pb-1 pt-0">
            {/* 5b: history visibility phrased as a benefit, at the moment it
                becomes true — inside the confirm, not a tooltip. */}
            <div className="bg-[#F7FAFF] border border-[#B9D6FF] rounded-[10px] px-3 py-2.5 text-xs text-[#344054] leading-relaxed">
              {selectedCount === 1 && selectedStaff[0] ? (
                <>
                  <span className="font-bold">
                    {selectedStaff[0].name ||
                      selectedStaff[0].email ||
                      `Account ${selectedStaff[0].account_id}`}
                  </span>
                  {selectedStaff[0].role ? ` (${selectedStaff[0].role})` : ""} will
                  be added and will see the{" "}
                  <span className="font-bold">full conversation history</span>.
                </>
              ) : (
                <>
                  <span className="font-bold">{selectedCount} members</span> will
                  be added and will see the{" "}
                  <span className="font-bold">full conversation history</span>.
                </>
              )}
            </div>
          </div>
        )}

        <div className="border-t border-[#E5E7EB] px-6 py-4 flex items-center justify-between bg-[#F9FAFB] rounded-b-2xl">
          <p className="text-xs text-[#6C737F]">
            {selectedCount
              ? `${selectedCount} member${selectedCount > 1 ? "s" : ""} selected`
              : "Select staff to invite"}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-[#111927] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F4F4F4] transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedCount}
              className="px-4 py-2 text-sm font-medium text-white bg-[#2283FF] rounded-lg hover:bg-[#1a6acc] transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              Add to conversation
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (!isOpen) {
    return null;
  }

  return createPortal(modalContent, document.body);
};

export default AddChatMembersModal;
